class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      const browse = facetForm.querySelector('.collection-browse-mode');
      if (browse) {
        browse.addEventListener('click', (event) => {
          const input = event.target.closest('.collection-sort-chips__chip')?.querySelector('input[name="browse"]');
          if (!input) return;
          this.submitBrowseMode(input.value);
        });
        browse.addEventListener('change', (event) => {
          if (event.target.name === 'browse') this.submitBrowseMode(event.target.value);
        });
      }

      facetForm.addEventListener(
        'pointerdown',
        (event) => {
          FacetFiltersForm.lockFilterChipScroll(event.target);
        },
        true
      );
      facetForm.addEventListener(
        'focusin',
        (event) => {
          FacetFiltersForm.pinFilterChipScroll(event.target);
        },
        true
      );
      facetForm.addEventListener('click', (event) => {
        FacetFiltersForm.scrollActiveFilterChip(event.target);
      });

      facetForm.addEventListener('input', (event) => {
        if (event.target.name === 'browse') return;
        if (event.target.matches('[data-trending-sort]')) {
          this.toggleTrendingSort(event.target.checked);
          return;
        }
        if (event.target.matches('input[type="radio"], input[type="checkbox"]')) {
          this.onSubmitHandler(event);
          return;
        }
        this.debouncedOnSubmit(event);
      });
    }

    FacetFiltersForm.syncCollectionFilterRows();
    this.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      FacetFiltersForm.resizeSortSelect(select);
      select.addEventListener('change', () => FacetFiltersForm.resizeSortSelect(select));
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static resizeSortSelect(select) {
    if (!(select instanceof HTMLSelectElement)) return;

    // Native <select> sizes to the longest option; shrink to the selected label
    // so the caret sits next to the visible text.
    const mirror = document.createElement('span');
    const styles = window.getComputedStyle(select);
    mirror.textContent = select.options[select.selectedIndex]?.text || select.value || '';
    mirror.setAttribute('aria-hidden', 'true');
    Object.assign(mirror.style, {
      position: 'absolute',
      visibility: 'hidden',
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      font: styles.font,
      fontSize: styles.fontSize,
      fontFamily: styles.fontFamily,
      fontWeight: styles.fontWeight,
      letterSpacing: styles.letterSpacing,
      textTransform: styles.textTransform,
    });
    document.body.appendChild(mirror);
    const caretStyles = window.getComputedStyle(select.closest('.select') || select);
    const caretGap = parseFloat(caretStyles.getPropertyValue('--backdrops-sort-caret-gap')) || 10;
    const caretSize = parseFloat(caretStyles.getPropertyValue('--backdrops-sort-caret-size')) || 11;
    const caretRoom = caretGap + caretSize;
    select.style.width = `${Math.ceil(mirror.getBoundingClientRect().width) + caretRoom}px`;
    mirror.remove();
  }

  static syncSortByInputs(searchParams) {
    const form = document.getElementById('FacetSortForm');
    if (!form) return;

    const urlSort = new URLSearchParams(searchParams).get('sort_by');
    const sortBy = urlSort || form.dataset.defaultSort;

    if (sortBy) {
      form.querySelectorAll('input[name="sort_by"]').forEach((input) => {
        input.checked = input.value === sortBy;
      });
      const select = form.querySelector('select[name="sort_by"]');
      if (select) {
        const hasOption = Array.from(select.options).some((option) => option.value === sortBy);
        if (hasOption) select.value = sortBy;
        FacetFiltersForm.resizeSortSelect(select);
      }
    }

    form.querySelectorAll('[data-trending-sort]').forEach((input) => {
      input.checked = urlSort === 'most-relevant';
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.syncSortByInputs(searchParams);
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCount');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const productGridContainer = document.getElementById('ProductGridContainer');
    const collectionEl = productGridContainer?.querySelector('.collection');
    if (collectionEl) collectionEl.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderFilterChips(html, event);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer') || document);
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderFilterChips(html, event);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer') || document);
    }
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderFilterChips(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const source = parsedHTML.getElementById('CollectionFilterChips');
    const target = document.getElementById('CollectionFilterChips');
    if (!source || !target) return;

    target.classList.remove('is-filter-loading');

    const isBrowseReset = !event || event.target?.name === 'browse' || event.browseReset;
    const eventTarget =
      event?.sortOnly || !(event?.target instanceof Element) ? null : event.target;
    const changedKey = FacetFiltersForm.lastFilterRowKey;
    const clickedRow = eventTarget?.closest('#CollectionFilterChips .collection-filter-chips__scroller');

    if (changedKey || clickedRow) {
      const sourceRows = Array.from(source.querySelectorAll('.collection-filter-chips__scroller'));
      const targetRows = FacetFiltersForm.getCollectionFilterRows();
      const logicalTarget = FacetFiltersForm.sortRowsForMode(targetRows);
      let changedIndex = changedKey
        ? logicalTarget.findIndex((row) => FacetFiltersForm.rowKey(row) === changedKey)
        : logicalTarget.indexOf(clickedRow);
      if (changedIndex < 0 && clickedRow) changedIndex = logicalTarget.indexOf(clickedRow);

      const keepKeys = new Set();
      if (changedIndex >= 0) {
        logicalTarget.forEach((row, index) => {
          if (index <= changedIndex) keepKeys.add(FacetFiltersForm.rowKey(row));
        });
      } else if (logicalTarget[0]) {
        keepKeys.add(FacetFiltersForm.rowKey(logicalTarget[0]));
      }

      targetRows.forEach((row) => {
        if (!keepKeys.has(FacetFiltersForm.rowKey(row))) row.remove();
      });

      FacetFiltersForm.sortRowsForMode(sourceRows).forEach((row) => {
        const key = FacetFiltersForm.rowKey(row);
        if (!key || keepKeys.has(key)) return;
        target.appendChild(document.importNode(row, true));
      });
    } else if (isBrowseReset) {
      target.innerHTML = source.innerHTML;
    }

    FacetFiltersForm.lastFilterRowKey = '';
    FacetFiltersForm.lastFilterRowIndex = -1;
    FacetFiltersForm.preservedFilterSelections = null;
    FacetFiltersForm.syncCollectionFilterRows();
  }

  static rowKey(row) {
    return row?.dataset.filterKind || row?.dataset.filterParam || '';
  }

  static sortRowsForMode(rows) {
    const mode = FacetFiltersForm.getBrowseMode();
    if (mode !== 'occasion') return rows.slice();
    return rows.slice().sort((left, right) => {
      const leftAge = left.dataset.filterKind === 'age' ? 0 : 1;
      const rightAge = right.dataset.filterKind === 'age' ? 0 : 1;
      return leftAge - rightAge;
    });
  }

  static getCollectionFilterRows() {
    return Array.from(document.querySelectorAll('#CollectionFilterChips .collection-filter-chips__scroller'));
  }

  static getBrowseMode() {
    const checked = document.querySelector('.collection-browse-mode input[name="browse"]:checked');
    if (checked?.value === 'theme' || checked?.value === 'occasion') return checked.value;

    const container = document.getElementById('CollectionFilterChips');
    if (container?.dataset.browseMode === 'theme' || container?.dataset.browseMode === 'occasion') {
      return container.dataset.browseMode;
    }

    try {
      const stored = sessionStorage.getItem('twinkleBrowseMode');
      if (stored === 'theme' || stored === 'occasion') return stored;
    } catch (error) {
      // sessionStorage can be blocked
    }

    return 'occasion';
  }

  static setBrowseMode(mode) {
    const nextMode = mode === 'theme' ? 'theme' : 'occasion';
    const container = document.getElementById('CollectionFilterChips');
    if (container) container.dataset.browseMode = nextMode;

    document.querySelectorAll('.collection-browse-mode input[name="browse"]').forEach((input) => {
      input.checked = input.value === nextMode;
    });

    try {
      sessionStorage.setItem('twinkleBrowseMode', nextMode);
    } catch (error) {
      // sessionStorage can be blocked
    }
  }

  static rowBelongsToMode(row, mode) {
    const tree = row.dataset.browseTree;
    if (!tree || tree === 'shared') return true;
    return tree === mode;
  }

  static getVisibleFilterRows() {
    return FacetFiltersForm.sortRowsForMode(
      FacetFiltersForm.getCollectionFilterRows().filter((row) =>
        FacetFiltersForm.rowBelongsToMode(row, FacetFiltersForm.getBrowseMode())
      )
    );
  }

  static resetAllFilterRows() {
    FacetFiltersForm.getCollectionFilterRows().forEach((row) => {
      FacetFiltersForm.resetFilterRow(row);
    });
  }

  static resetInactiveBrowseRows() {
    const mode = FacetFiltersForm.getBrowseMode();
    FacetFiltersForm.getCollectionFilterRows().forEach((row) => {
      if (!FacetFiltersForm.rowBelongsToMode(row, mode)) {
        FacetFiltersForm.resetFilterRow(row);
        row.setAttribute('hidden', '');
      }
    });
  }

  static normalizeChipHandle(value) {
    return `${value || ''}`
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  static chipHandles(input) {
    const valueHandle = FacetFiltersForm.normalizeChipHandle(input?.value);
    const labelHandle = FacetFiltersForm.normalizeChipHandle(FacetFiltersForm.chipLabelText(input?.closest('label')));
    return [valueHandle, labelHandle].filter(Boolean);
  }

  static chipText(input) {
    return `${input?.value || ''} ${FacetFiltersForm.chipLabelText(input?.closest('label'))}`;
  }

  static isKidsAgeSelection() {
    const ageRow = FacetFiltersForm.getCollectionFilterRows().find((row) => row.dataset.filterKind === 'age');
    const selected = ageRow?.querySelector('input:checked');
    if (!selected?.value) return false;
    const text = FacetFiltersForm.chipText(selected);
    if (text.includes('أطفال')) return true;
    return FacetFiltersForm.chipHandles(selected).some((handle) => handle === 'kids' || handle === 'kid');
  }

  static isBabiesAgeSelection() {
    const ageRow = FacetFiltersForm.getCollectionFilterRows().find((row) => row.dataset.filterKind === 'age');
    const selected = ageRow?.querySelector('input:checked');
    if (!selected?.value) return false;
    const text = FacetFiltersForm.chipText(selected);
    if (/مولود|مواليد|رضيع|رضع/.test(text)) return true;
    return FacetFiltersForm.chipHandles(selected).some((handle) => handle === 'babies' || handle === 'baby');
  }

  static isAdultsAgeSelection() {
    const ageRow = FacetFiltersForm.getCollectionFilterRows().find((row) => row.dataset.filterKind === 'age');
    const selected = ageRow?.querySelector('input:checked');
    if (!selected?.value) return false;
    const text = FacetFiltersForm.chipText(selected);
    if (/بالغ|كبار/.test(text)) return true;
    return FacetFiltersForm.chipHandles(selected).some((handle) => handle === 'adults' || handle === 'adult');
  }

  static isAgeAudienceInput(input) {
    if (!(input instanceof HTMLInputElement) || !input.value) return false;
    const row = input.closest('#CollectionFilterChips .collection-filter-chips__scroller');
    if (row?.dataset.filterKind !== 'age') return false;

    const handles = FacetFiltersForm.chipHandles(input);
    if (handles.some((handle) => ['babies', 'baby', 'kids', 'kid', 'adults', 'adult'].includes(handle))) {
      return true;
    }

    const text = FacetFiltersForm.chipText(input);
    if (/مولود|مواليد|رضيع|رضع|أطفال|بالغ|كبار/.test(text)) return true;
    return /\bbabies\b|\bbaby\b|\bkids\b|\bkid\b|\badults\b|\badult\b/i.test(text);
  }

  static preselectTrendingForAgeAudience(form) {
    if (!form) return;
    form.querySelectorAll('[data-trending-sort]').forEach((input) => {
      input.checked = true;
    });
    const select = form.querySelector('select[name="sort_by"]');
    if (!select) return;
    const hasOption = Array.from(select.options).some((option) => option.value === 'most-relevant');
    if (!hasOption) return;
    select.value = 'most-relevant';
    FacetFiltersForm.resizeSortSelect(select);
  }

  static isAllowedKidsOccasion(input) {
    if (!input?.value) return true;
    const handles = FacetFiltersForm.chipHandles(input);
    if (handles.some((handle) => handle === 'first-birthday' || handle === 'first-birthdays')) return false;
    const text = FacetFiltersForm.chipText(input);
    if (/ميلاد/.test(text) && /أول|الاول|الأول|first/i.test(text)) return false;
    const allowed = new Set([
      'birthdays',
      'birthday',
      'happy-birthday',
      'kids-birthdays',
      'kids-birthday',
      'congratulations',
      'kids-congratulations',
    ]);
    if (/ميلاد/.test(text) || /تهاني|مبروك/.test(text)) return true;
    return handles.some((handle) => allowed.has(handle));
  }

  static isHiddenBabiesOccasion(input) {
    if (!input?.value) return false;
    const handles = FacetFiltersForm.chipHandles(input);
    if (handles.some((handle) => handle === 'first-steps' || handle === 'first-step')) return false;
    if (handles.some((handle) => handle === 'first-birthday' || handle === 'first-birthdays')) return false;
    const text = FacetFiltersForm.chipText(input);
    if (/خطوات/.test(text)) return false;
    if (/ميلاد/.test(text) && /أول|الاول|الأول|first/i.test(text)) return false;
    const hidden = new Set(['birthdays', 'birthday', 'happy-birthday', 'congratulations']);
    if (/تهاني|مبروك/.test(text)) return true;
    if (/ميلاد/.test(text)) return true;
    return handles.some((handle) => hidden.has(handle));
  }

  static applyKidsOccasionAllowlist() {
    const kidsSelected = FacetFiltersForm.isKidsAgeSelection();
    const babiesSelected = FacetFiltersForm.isBabiesAgeSelection();
    FacetFiltersForm.getCollectionFilterRows().forEach((row) => {
      if (row.dataset.browseTree !== 'occasion') return;

      row.querySelectorAll('.collection-sort-chips__chip').forEach((chip) => {
        const input = chip.querySelector('input');
        if (input?.hasAttribute('data-trending-sort')) return;
        if (!input?.value) {
          chip.removeAttribute('hidden');
          return;
        }
        let allowed = true;
        if (kidsSelected) allowed = FacetFiltersForm.isAllowedKidsOccasion(input);
        else if (babiesSelected) allowed = !FacetFiltersForm.isHiddenBabiesOccasion(input);
        chip.toggleAttribute('hidden', !allowed);
        if (!allowed && input.checked) input.checked = false;
      });

      const hasVisibleValue = Array.from(row.querySelectorAll('input:checked')).some(
        (input) =>
          input.value &&
          !input.hasAttribute('data-trending-sort') &&
          !input.closest('.collection-sort-chips__chip')?.hasAttribute('hidden')
      );
      if (!hasVisibleValue) {
        const allInput = row.querySelector('input[value=""]');
        if (allInput) allInput.checked = true;
      }
    });
  }

  static filterRowHasValue(row) {
    return Array.from(row.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked')).some(
      (input) => input.name !== 'sort_by' && !input.hasAttribute('data-trending-sort') && input.value !== ''
    );
  }

  static filterRowOptionCount(row) {
    return Array.from(row.querySelectorAll('input[type="radio"], input[type="checkbox"]')).filter(
      (input) =>
        input.name !== 'sort_by' &&
        !input.hasAttribute('data-trending-sort') &&
        input.value !== '' &&
        !input.closest('.collection-sort-chips__chip')?.hasAttribute('hidden')
    ).length;
  }

  static filterRowHasMultipleOptions(row) {
    return FacetFiltersForm.filterRowOptionCount(row) > 1;
  }

  static resetFilterRow(row) {
    const inputs = row.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    const allInput = row.querySelector('input[value=""]');
    inputs.forEach((input) => {
      if (input.name === 'sort_by' || input.hasAttribute('data-trending-sort')) return;
      input.checked = allInput ? input === allInput : false;
    });
  }

  static chipLabelText(root) {
    return root?.querySelector('.collection-sort-chips__label')?.textContent.trim() || '';
  }

  static copyChipIcon(fromRoot, toRoot) {
    const source = fromRoot?.matches?.('.collection-filter-chips__icon')
      ? fromRoot
      : fromRoot?.querySelector('.collection-filter-chips__icon');
    const target = toRoot?.querySelector('.collection-filter-chips__icon');
    if (!source || !target) return;
    target.replaceWith(source.cloneNode(true));
  }

  static restoreAllIcon(allFace) {
    const fallback = document.querySelector(
      '#CollectionFilterChips .collection-filter-chips__icon-fallback .collection-filter-chips__icon'
    );
    FacetFiltersForm.copyChipIcon(fallback, allFace);
  }

  static updateFilterAllLabels() {
    const container = document.getElementById('CollectionFilterChips');
    const rows = FacetFiltersForm.getVisibleFilterRows().filter((row) => !row.hasAttribute('hidden'));
    if (!container || !rows.length) return;

    const allText = container.dataset.filterAll || '';
    const allTemplate = container.dataset.filterAllTemplate || '';

    rows.forEach((row, index) => {
      const allFace = row.querySelector('.collection-filter-chips__all-face');
      if (!allFace) return;
      const labelEl = allFace.querySelector('.collection-sort-chips__label');

      if (index === 0) {
        if (labelEl) labelEl.textContent = allText;
        else allFace.textContent = allText;
        FacetFiltersForm.restoreAllIcon(allFace);
        return;
      }

      let parentLabel = '';
      let parentChip = null;
      for (let i = index - 1; i >= 0; i--) {
        if (rows[i].hasAttribute('hidden')) continue;
        const selected = rows[i].querySelector('input:checked');
        if (selected?.value) {
          parentChip = selected.closest('label');
          parentLabel = FacetFiltersForm.chipLabelText(parentChip);
          break;
        }
      }

      if (parentChip) FacetFiltersForm.copyChipIcon(parentChip, allFace);
      else FacetFiltersForm.restoreAllIcon(allFace);
      if (labelEl) {
        labelEl.textContent =
          parentLabel && allTemplate ? allTemplate.replace('__LABEL__', parentLabel) : allText;
      } else {
        allFace.textContent =
          parentLabel && allTemplate ? allTemplate.replace('__LABEL__', parentLabel) : allText;
      }
    });
  }

  static syncCollectionFilterRows() {
    const allRows = FacetFiltersForm.getCollectionFilterRows();
    if (!allRows.length) return;

    const mode = FacetFiltersForm.getBrowseMode();
    FacetFiltersForm.setBrowseMode(mode);
    FacetFiltersForm.resetInactiveBrowseRows();
    FacetFiltersForm.applyKidsOccasionAllowlist();

    const rows = FacetFiltersForm.getVisibleFilterRows();
    const maxLevels = mode === 'theme' ? 1 : 2;
    let revealNext = true;
    let shownLevels = 0;
    rows.forEach((row) => {
      const isParentLevel = shownLevels === 0 && revealNext;
      const useful = FacetFiltersForm.filterRowHasMultipleOptions(row) || isParentLevel;

      if (!useful) {
        row.setAttribute('hidden', '');
        FacetFiltersForm.resetFilterRow(row);
        return;
      }

      const shouldShow = revealNext && shownLevels < maxLevels;
      row.toggleAttribute('hidden', !shouldShow);

      if (!shouldShow) {
        FacetFiltersForm.resetFilterRow(row);
        revealNext = false;
        return;
      }

      shownLevels += 1;
      revealNext = FacetFiltersForm.filterRowHasValue(row) && shownLevels < maxLevels;
    });

    FacetFiltersForm.updateFilterAllLabels();
    FacetFiltersForm.resetChipStackScroll();
    FacetFiltersForm.flushFilterChipScroll();
  }

  static isMobileFilterChipLayout() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  static filterChipFromTarget(source) {
    return source instanceof Element
      ? source.closest('#CollectionFilterChips .collection-sort-chips__chip')
      : null;
  }

  static lockFilterChipScroll(source) {
    if (!FacetFiltersForm.isMobileFilterChipLayout()) return;
    const chip = FacetFiltersForm.filterChipFromTarget(source);
    const scroller = chip?.closest('#CollectionFilterChips .collection-filter-chips__scroller');
    const input = chip?.querySelector('input');
    if (!chip || !scroller || scroller.hasAttribute('hidden')) return;

    FacetFiltersForm.chipScrollLock = { scroller, left: scroller.scrollLeft };
    if (input) input.focus({ preventScroll: true });
    scroller.scrollLeft = FacetFiltersForm.chipScrollLock.left;
  }

  static pinFilterChipScroll(source) {
    const lock = FacetFiltersForm.chipScrollLock;
    if (!lock?.scroller) return;
    if (FacetFiltersForm.chipScrollAnims.get(lock.scroller)) return;
    if (source && !FacetFiltersForm.filterChipFromTarget(source) && source !== lock.scroller) {
      return;
    }
    lock.scroller.scrollLeft = lock.left;
  }

  static scrollActiveFilterChip(source) {
    if (!FacetFiltersForm.isMobileFilterChipLayout()) return;

    const chip = FacetFiltersForm.filterChipFromTarget(source);
    if (!chip || chip.hasAttribute('hidden')) return;

    const scroller = chip.closest('#CollectionFilterChips .collection-filter-chips__scroller');
    if (!scroller || scroller.hasAttribute('hidden')) return;

    FacetFiltersForm.pendingChipScrollEl = chip;
    const input = chip.querySelector('input');
    if (input) input.focus({ preventScroll: true });
    FacetFiltersForm.pinFilterChipScroll(chip);

    requestAnimationFrame(() => {
      FacetFiltersForm.pinFilterChipScroll(chip);
      requestAnimationFrame(() => {
        FacetFiltersForm.pinFilterChipScroll(chip);
        FacetFiltersForm.applyFilterChipScroll(chip);
      });
    });
  }

  static applyFilterChipScroll(chip) {
    if (!(chip instanceof Element) || !chip.isConnected || chip.hasAttribute('hidden')) return;
    if (!FacetFiltersForm.isMobileFilterChipLayout()) return;

    const scroller = chip.closest('#CollectionFilterChips .collection-filter-chips__scroller');
    if (!scroller || scroller.hasAttribute('hidden')) return;
    if (FacetFiltersForm.chipScrollAnims.get(scroller)) return;
    if (scroller.scrollWidth - scroller.clientWidth <= 2) return;

    const lock = FacetFiltersForm.chipScrollLock;
    const from = lock?.scroller === scroller ? lock.left : scroller.scrollLeft;
    scroller.scrollLeft = from;

    const scrollerRect = scroller.getBoundingClientRect();
    const chips = Array.from(scroller.querySelectorAll('.collection-sort-chips__chip')).filter(
      (el) => !el.hasAttribute('hidden')
    );
    const lastChip = chips[chips.length - 1];
    if (!lastChip) return;

    const isRTL = getComputedStyle(scroller).direction === 'rtl';
    const lastRect = lastChip.getBoundingClientRect();
    const atEnd = isRTL
      ? lastRect.left >= scrollerRect.left - 2
      : lastRect.right <= scrollerRect.right + 2;
    if (atEnd) return;

    const chipRect = chip.getBoundingClientRect();
    const styles = getComputedStyle(scroller);
    const paddingStart = parseFloat(isRTL ? styles.paddingRight : styles.paddingLeft) || 0;
    const delta = isRTL
      ? chipRect.right - (scrollerRect.right - paddingStart)
      : chipRect.left - (scrollerRect.left + paddingStart);
    if (Math.abs(delta) <= 2) return;

    FacetFiltersForm.animateFilterChipScroll(scroller, from + delta, from);
  }

  static animateFilterChipScroll(scroller, targetLeft, fromLeft) {
    const from = Number.isFinite(fromLeft) ? fromLeft : scroller.scrollLeft;
    if (Math.abs(from - targetLeft) <= 1) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      scroller.scrollLeft = targetLeft;
      FacetFiltersForm.chipScrollLock = null;
      return;
    }

    const running = FacetFiltersForm.chipScrollAnims.get(scroller);
    if (running && Math.abs(running.target - targetLeft) <= 2) return;
    if (running?.rafId) cancelAnimationFrame(running.rafId);

    const change = targetLeft - from;
    const duration = 700;
    const startTime = performance.now();
    const ease = (t) => -(Math.cos(Math.PI * t) - 1) / 2;
    const anim = { target: targetLeft, rafId: 0 };

    const step = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      scroller.scrollLeft = from + change * ease(t);
      if (t < 1) {
        anim.rafId = requestAnimationFrame(step);
        return;
      }
      scroller.scrollLeft = targetLeft;
      FacetFiltersForm.chipScrollAnims.delete(scroller);
      if (FacetFiltersForm.chipScrollLock?.scroller === scroller) {
        FacetFiltersForm.chipScrollLock = null;
      }
    };

    anim.rafId = requestAnimationFrame(step);
    FacetFiltersForm.chipScrollAnims.set(scroller, anim);
  }

  static flushFilterChipScroll() {
    const chip = FacetFiltersForm.pendingChipScrollEl;
    if (!(chip instanceof Element) || !chip.isConnected) return;
    const scroller = chip.closest('#CollectionFilterChips .collection-filter-chips__scroller');
    if (scroller && FacetFiltersForm.chipScrollAnims.get(scroller)) return;
    requestAnimationFrame(() => {
      FacetFiltersForm.pinFilterChipScroll(chip);
      FacetFiltersForm.applyFilterChipScroll(chip);
    });
  }

  static resetChipStackScroll() {
    const stack = document.querySelector('#FacetSortForm .collection-sort-chips');
    if (stack) stack.scrollLeft = 0;
  }

  static prepareCollectionFilterRows(event) {
    const rows = FacetFiltersForm.getCollectionFilterRows();
    if (!rows.length) return;

    const activeRow = event?.target instanceof Element
      ? event.target.closest('#CollectionFilterChips .collection-filter-chips__scroller')
      : null;
    if (!activeRow) return;

    const logical = FacetFiltersForm.sortRowsForMode(rows);
    const activeIndex = logical.indexOf(activeRow);
    if (activeIndex < 0) return;

    FacetFiltersForm.lastFilterRowKey = FacetFiltersForm.rowKey(activeRow);
    FacetFiltersForm.lastFilterRowIndex = activeIndex;

    const container = document.getElementById('CollectionFilterChips');
    if (container) container.classList.add('is-filter-loading');

    logical.forEach((row, index) => {
      if (index > activeIndex) {
        FacetFiltersForm.resetFilterRow(row);
        row.setAttribute('hidden', '');
      }
    });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const countSource = parsedHTML.getElementById('ProductCount') || parsedHTML.getElementById('ProductCount');
    if (!countSource) return;

    const count = countSource.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCount');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event?.target instanceof Element ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const currentActiveFacetsElement = document.querySelector(selector);
      if (!activeFacetsElement || !currentActiveFacetsElement) return;
      currentActiveFacetsElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '[data-submit-label]'];
    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form, options = {}) {
    const formData = new FormData(form);
    const params = new URLSearchParams();
    const clearFilters = Boolean(options.clearFilters);
    const hiddenParams = new Set();
    if (!clearFilters) {
      form.querySelectorAll('#CollectionFilterChips .collection-filter-chips__scroller[hidden] [name]').forEach((input) => {
        if (input.name) hiddenParams.add(input.name);
      });
    }
    for (const [key, value] of formData.entries()) {
      if (key === 'browse' || value === '') continue;
      if (clearFilters && key.startsWith('filter.')) continue;
      if (hiddenParams.has(key)) continue;
      params.append(key, value);
    }
    if (form.querySelector('[data-trending-sort]:checked')) params.set('sort_by', 'most-relevant');
    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  submitBrowseMode(mode, options = {}) {
    const nextMode = mode === 'theme' ? 'theme' : 'occasion';
    const now = Date.now();
    if (this._lastBrowseSubmit === nextMode && now - this._lastBrowseSubmitAt < 400) return;
    this._lastBrowseSubmit = nextMode;
    this._lastBrowseSubmitAt = now;

    FacetFiltersForm.setBrowseMode(nextMode);
    FacetFiltersForm.lastFilterRowKey = '';
    FacetFiltersForm.lastFilterRowIndex = -1;
    FacetFiltersForm.pendingChipScrollEl = null;
    FacetFiltersForm.chipScrollLock = null;
    FacetFiltersForm.preservedFilterSelections = null;
    FacetFiltersForm.resetAllFilterRows();
    FacetFiltersForm.syncCollectionFilterRows();

    const form = this.querySelector('form') || document.getElementById('FacetSortForm');
    if (options.resetSort) {
      form?.querySelectorAll('[data-trending-sort]').forEach((input) => {
        input.checked = false;
      });
      const select = form?.querySelector('select[name="sort_by"]');
      const defaultSort = form?.dataset.defaultSort;
      if (select && defaultSort) {
        select.value = defaultSort;
        FacetFiltersForm.resizeSortSelect(select);
      }
    }

    const params = new URLSearchParams(this.createSearchParams(form, { clearFilters: true }));
    if (options.resetSort) params.delete('sort_by');
    this.onSubmitForm(params.toString(), { target: { name: 'browse', value: nextMode }, browseReset: true });
  }

  toggleTrendingSort(enabled) {
    const form = this.querySelector('form') || document.getElementById('FacetSortForm');
    form?.querySelectorAll('[data-trending-sort]').forEach((input) => {
      input.checked = enabled;
    });
    const params = new URLSearchParams(this.createSearchParams(form));
    if (enabled) params.set('sort_by', 'most-relevant');
    else params.delete('sort_by');
    this.onSubmitForm(params.toString(), {
      target: form?.querySelector('[data-trending-sort]'),
      sortOnly: true,
    });
  }

  static isTopLevelAllChip(event) {
    const input = event?.target;
    if (!(input instanceof HTMLInputElement) || input.value !== '') return false;
    const activeRow = input.closest('#CollectionFilterChips .collection-filter-chips__scroller');
    if (!activeRow) return false;
    const logical = FacetFiltersForm.getVisibleFilterRows();
    return logical[0] === activeRow;
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (event.target?.name === 'browse') {
      this.submitBrowseMode(event.target.value);
      return;
    }
    if (event.target?.name === 'sort_by') {
      const form = event.target.closest('form') || document.getElementById('FacetSortForm');
      if (event.target.tagName === 'SELECT') {
        form?.querySelectorAll('[data-trending-sort]').forEach((input) => {
          input.checked = event.target.value === 'most-relevant';
        });
      }
      const forms = [];
      document.querySelectorAll('facet-filters-form form').forEach((formEl) => {
        if (formEl.id === 'FacetSortForm' || formEl.id === 'FacetFiltersForm' || formEl.id === 'FacetSortDrawerForm') {
          forms.push(this.createSearchParams(formEl));
        }
      });
      this.onSubmitForm(forms.join('&'), { target: event.target, sortOnly: true });
      return;
    }
    if (FacetFiltersForm.isTopLevelAllChip(event)) {
      this._lastBrowseSubmit = '';
      this.submitBrowseMode(FacetFiltersForm.getBrowseMode(), { resetSort: true });
      return;
    }
    if (FacetFiltersForm.isAgeAudienceInput(event.target)) {
      const form = event.target.closest('form') || document.getElementById('FacetSortForm');
      FacetFiltersForm.preselectTrendingForAgeAudience(form);
    }
    FacetFiltersForm.prepareCollectionFilterRows(event);
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.lastFilterRowKey = '';
FacetFiltersForm.lastFilterRowIndex = -1;
FacetFiltersForm.pendingChipScrollEl = null;
FacetFiltersForm.chipScrollLock = null;
FacetFiltersForm.chipScrollAnims = new WeakMap();
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
}
FacetFiltersForm.setListeners();
FacetFiltersForm.syncCollectionFilterRows();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove);
}
